import { useCallback, useMemo, useRef, useState } from 'react';
import { ModalTableMultiselectMode, TableTabs } from '../enums';

type AllTabState<T> = {
  collection: T[];
  totalCount: number;
};

type HandleAllTabQueryResultArgs<T> = {
  collection: T[];
  totalCount: number;
  loading: boolean;
  firstLoading: boolean;
};

export type AllTabSnapshot<T> = {
  collection: T[];
  totalCount: number;
  firstLoading: boolean;
};

type UseModalTableAllTabManagerArgs = {
  isOpen: boolean;
  mode: ModalTableMultiselectMode;
  activeTab: TableTabs;
  isSelectedOnlyMode: boolean;
};

/**
 * Shared helper that keeps track of the All tab query lifecycle.
 * It ensures we trigger the initial fetch for WithTabs mode once, cache the last resolved payload,
 * and expose helpers to derive the effective snapshot (prefetched vs live) that aligns with the
 * wrapper-specific GraphQL hook results.
 */
export default function useModalTableAllTabManager<T>({
  isOpen,
  mode,
  activeTab,
  isSelectedOnlyMode,
}: UseModalTableAllTabManagerArgs) {
  const [shouldRunInitialAllQuery, setShouldRunInitialAllQuery] = useState(
    () => mode === ModalTableMultiselectMode.WithTabs,
  );

  const prefetchedAllStateRef = useRef<AllTabState<T>>({
    collection: [],
    totalCount: 0,
  });

  const shouldRunAllQuery = useMemo(() => {
    if (isSelectedOnlyMode || !isOpen) {
      return false;
    }
    return (
      activeTab === TableTabs.All ||
      (mode === ModalTableMultiselectMode.WithTabs && shouldRunInitialAllQuery)
    );
  }, [activeTab, isOpen, isSelectedOnlyMode, mode, shouldRunInitialAllQuery]);

  const handleAllTabQueryResult = useCallback(
    ({
      collection,
      totalCount,
      loading,
      firstLoading,
    }: HandleAllTabQueryResultArgs<T>) => {
      if (
        mode === ModalTableMultiselectMode.WithTabs &&
        shouldRunInitialAllQuery &&
        shouldRunAllQuery &&
        !firstLoading
      ) {
        setShouldRunInitialAllQuery(false);
      }

      if (shouldRunAllQuery && !loading) {
        prefetchedAllStateRef.current = {
          collection,
          totalCount,
        };
      }
    },
    [mode, shouldRunAllQuery, shouldRunInitialAllQuery],
  );

  const getAllTabSnapshot = useCallback(
    ({
      collection,
      totalCount,
      firstLoading,
    }: {
      collection: T[];
      totalCount: number;
      firstLoading: boolean;
    }): AllTabSnapshot<T> => {
      if (shouldRunAllQuery) {
        return {
          collection,
          totalCount,
          firstLoading,
        };
      }

      const { collection: cachedCollection, totalCount: cachedTotalCount } =
        prefetchedAllStateRef.current;

      return {
        collection: cachedCollection,
        totalCount: cachedTotalCount,
        firstLoading: false,
      };
    },
    [shouldRunAllQuery],
  );

  return {
    shouldRunAllQuery,
    handleAllTabQueryResult,
    getAllTabSnapshot,
  };
}
